import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Accordion, Breadcrumbs, EmptyState, Input } from '../../components';
import useDocumentHead from '../../hooks/useDocumentHead';
import { useLang } from '../../i18n';
import { get } from '../../services/api';
import './FaqPage.scss';

interface FaqItem { id: string | number; question: string; answer: string; category?: string }

export default function FaqPage() {
  const { t } = useLang();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const res = await get<{ items?: FaqItem[] }>('/faq/');
        if (!cancel) setItems(res?.items || []);
      } catch {
        if (!cancel) setItems([]);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.toLowerCase();
    return items.filter((it) =>
      it.question.toLowerCase().includes(q) || it.answer.toLowerCase().includes(q),
    );
  }, [items, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, FaqItem[]>();
    for (const it of filtered) {
      const key = it.category || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(it);
    }
    return Array.from(map.entries());
  }, [filtered]);

  useDocumentHead({
    title: `${t('faq.title')} — ${t('brand.name')}`,
    description: t('faq.sub'),
    canonical: 'https://ukam.uz/faq',
    og: { title: t('faq.title'), description: t('faq.sub'), type: 'website' },
    schemaOrg: {
      '@context': 'https://schema.org', '@type': 'FAQPage',
      mainEntity: items.map((f) => ({
        '@type': 'Question', name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  });

  return (
    <div className="faq-page">
      <div className="container faq-page__head">
        <Breadcrumbs items={[{ label: t('brand.name'), href: '/' }, { label: t('faq.title') }]} />
        <h1 className="faq-page__title">{t('faq.title')}</h1>
        <p className="faq-page__sub">{t('faq.sub')}</p>
        <div className="faq-page__search">
          <Input
            placeholder={t('faq.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            iconLeft={<Search size={18} />}
          />
        </div>
      </div>
      <section className="container section">
        {filtered.length === 0 ? (
          <EmptyState title={t('faq.empty')} />
        ) : (
          <div className="faq-page__groups">
            {grouped.map(([cat, list]) => (
              <div key={cat} className="faq-page__group">
                {cat && <h2 className="faq-page__group-title">{cat}</h2>}
                <Accordion items={list.map((it) => ({
                  id: String(it.id),
                  title: it.question,
                  content: it.answer,
                }))} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
